/**
 * ChatInputBar Component
 * 
 * Message input with camera button, image preview, and send button.
 * Handles keyboard shortcuts (Enter to send).
 * Integrated with ChatImageUpload for inline image attachment.
 * 
 * Extracted from Figma: ORO - Nightfall Kit - ALL Pages
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7A
 */

import { Send, X, Megaphone } from 'lucide-react';
import { useState, useRef } from 'react';
import ChatImageUpload from './ChatImageUpload';
import { OroLoader } from './OroLoader';
import { getZIndexClass } from '../config/zIndex';

interface UploadedImage {
  id: string;
  url: string;
  file: File;
  isUploading: boolean;
}

interface ChatInputBarProps {
  // Returns false on a FAILED send (so the composer can restore text + the
  // announcement arming for retry); void/true = success (clear). #1445.
  onSendMessage?: (message: string, isAnnouncement: boolean) => void | Promise<boolean | void>;
  attachedImages?: UploadedImage[];
  onAttachedImagesChange?: (images: UploadedImage[] | ((prev: UploadedImage[]) => UploadedImage[])) => void;
  onRemoveImage?: (imageId: string) => void;
  clubId?: string;
  isSending?: boolean;
  containerRef?: React.Ref<HTMLDivElement>;
  // #1445/#2759: true for leaders/owners (announcements are free for every
  // club — tier no longer gates them; hint-driven from
  // Room.can_announce). Gates whether the announcement toggle renders at all —
  // it is a Configuration-layer affordance that adds ZERO weight for the 90%
  // (regular members simply never see it). The send handler is the enforcement
  // half (Core Rule 48 dual-site); this only drives the composer affordance.
  canAnnounce?: boolean;
}

export default function ChatInputBar({
  onSendMessage,
  attachedImages = [],
  onAttachedImagesChange,
  onRemoveImage,
  clubId,
  isSending = false,
  containerRef,
  canAnnounce = false,
}: ChatInputBarProps) {
  const [message, setMessage] = useState('');
  // #1445: whether the next send is flagged as an announcement. Local composer
  // state — no persistence (feedback_simple_solutions).
  const [isAnnouncement, setIsAnnouncement] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isUploading = attachedImages.some(img => img.isUploading);
  const hasText = message.trim().length > 0;

  // Only ever treat the send as an announcement when the room actually permits
  // it. Guards a stuck toggle if `canAnnounce` flips false after toggling on
  // (defensive — no useEffect needed).
  const announcementActive = canAnnounce && isAnnouncement;

  // #1445: an armed announcement REQUIRES non-empty trimmed text. The backend
  // announce path rejects an image-only announcement (400), so an armed +
  // image-only composer would render an enabled Send that always fails. Gate
  // it here: when armed, text is mandatory; otherwise text OR an image sends.
  const canSend =
    (announcementActive ? hasText : hasText || attachedImages.length > 0) &&
    !isUploading &&
    !isSending;

  const handleSend = () => {
    if (!canSend || !onSendMessage) return;
    const textToSend = message.trim();
    // Snapshot the raw toggle state (not `announcementActive`) so a failed send
    // restores exactly what the user had armed.
    const wasAnnouncement = isAnnouncement;
    // Clear optimistically on dispatch (the input is disabled while sending, so
    // no newer draft can be clobbered). An announcement is a deliberate,
    // per-message action, not a sticky mode — so it resets on dispatch too.
    setMessage('');
    setIsAnnouncement(false);
    inputRef.current?.focus();
    // #1445: on a FAILED send, restore BOTH the text and the announcement
    // arming. Without this, the reset-on-dispatch silently downgraded a failed
    // announcement into a plain message on retry. Reset-on-success is unchanged
    // (a void/true result leaves the composer cleared).
    void Promise.resolve(onSendMessage(textToSend, announcementActive)).then((ok) => {
      if (ok === false) {
        setMessage((current) => (current.length === 0 ? textToSend : current));
        setIsAnnouncement(wasAnnouncement);
      }
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div 
      ref={containerRef}
      className={`fixed inset-x-0 px-4 py-3 border-t border-oro-border bg-oro-surface ${getZIndexClass('chatInput')}`}
      style={{
        bottom: 'calc(max(var(--oro-bottom-nav-height, 64px), 64px) + max(env(safe-area-inset-bottom), 0px))',
      }}
    >
      {/* #1445: lightweight announcement affordance. The hint only shows while
          armed, so the composer stays quiet for a normal message. */}
      {announcementActive && (
        <div
          data-testid="announcement-hint"
          className="mb-2 flex items-center gap-1.5 text-xs text-accent"
        >
          <Megaphone className="w-3.5 h-3.5 shrink-0" />
          {/* #1445: when armed without text the Send button is disabled (an
              announcement can't be image-only). Say why, reusing the same
              lightweight hint slot rather than adding new UI weight. */}
          <span>
            {hasText
              ? 'This message will be posted as an announcement'
              : 'Add a message — announcements can’t be image-only'}
          </span>
        </div>
      )}

      {/* Image preview section */}
      {attachedImages.length > 0 && (
        <div className="mb-3 flex gap-2 overflow-x-auto">
          {attachedImages.map((image) => (
            <div key={image.id} className="relative shrink-0">
              <img
                src={image.url}
                alt=""
                className="w-16 h-16 rounded-lg object-cover border border-oro-border"
              />
              {image.isUploading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                  <OroLoader variant="inline" color="currentColor" className="text-white" label="Uploading" />
                </div>
              )}
              <button
                onClick={() => onRemoveImage?.(image.id)}
                className="absolute -top-1 -right-1 w-5 h-5 bg-oro-error rounded-full flex items-center justify-center text-white text-xs hover:bg-oro-error/80 transition-colors"
                aria-label="Remove image"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      
      <div className="flex items-center gap-3">
        {/* Camera button with integrated upload */}
        {clubId && onAttachedImagesChange && (
          <ChatImageUpload
            clubId={clubId}
            images={attachedImages}
            onImagesChange={onAttachedImagesChange}
            maxImages={4}
            disabled={attachedImages.length >= 4}
          />
        )}

        {/* #1445/#2759: announcement toggle — leaders/owners only (canAnnounce);
            announcements are free for every club (tier no longer gates them).
            Megaphone, no emoji (Rule 20). Accent-filled when armed so the state
            reads clearly without adding a heavy control. */}
        {canAnnounce && (
          <button
            type="button"
            onClick={() => setIsAnnouncement((v) => !v)}
            aria-pressed={isAnnouncement}
            aria-label={
              isAnnouncement
                ? 'Announcement on: this message will be posted as an announcement'
                : 'Post as announcement'
            }
            title={isAnnouncement ? 'Sending as announcement' : 'Post as announcement'}
            data-testid="announcement-toggle"
            className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all ${
              isAnnouncement
                ? 'bg-accent text-oro-bg shadow-glow'
                : 'bg-oro-surface-alt text-text-secondary hover:text-text-primary'
            }`}
          >
            <Megaphone className="w-5 h-5" />
          </button>
        )}

        {/* Text input */}
        <div className="flex-1">
          <input
            ref={inputRef}
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            disabled={isSending}
            className="w-full h-11 px-4 rounded-full outline-none transition-all bg-oro-surface-alt text-text-primary border border-transparent focus:border-2 focus:border-accent shadow-oro-soft disabled:opacity-50"
          />
        </div>

        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={!canSend}
          className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed bg-accent border-0 shadow-oro-soft"
          aria-label="Send message"
        >
          {isSending ? (
            <OroLoader variant="inline" color="currentColor" className="text-oro-bg" label="Sending" />
          ) : (
            <Send className="w-5 h-5 text-oro-bg" />
          )}
        </button>
      </div>
    </div>
  );
}
